<!-- This widget visualizes button text and size fields together. -->

<template>
  <v-form
    ref="form"
    class="px-2 grow-1"
    @submit.prevent
  >
    <!-- Optional warning messages -->
    <v-layout
      v-for="(warning, warningIndex) in warnings"
      :key="'widget-field-warning-' + warningIndex"
      align-center
      class="mb-2"
    >
      <v-icon
        color="warning"
        class="mr-2"
      >
        error_outline
      </v-icon>
      <span class="warning--text">
        {{ warning.prefix }}: <b>{{ warning.fieldLabel }}</b>
      </span>
    </v-layout>
    <v-layout column>
      <!-- Button Text -->
      <overlay-data-text-field
        :field="textField"
        :value.sync="textField.value"
        @update:value="$emit('changed')"
      />
      <!-- Colors (optional) -->
      <v-layout
        v-if="!!textColorField || !!backgroundColorField"
        align-center
        class="overlay-options-field-bg"
      >
        <!-- Text Color (optional) -->
        <div v-if="!!textColorField && isColorField(textColorField)">
          <overlay-data-color-field
            :color-palette="textColorFieldPalette"
            :field="textColorField"
            :render-key="'widget-button-text-color-' + colorfieldRenderKey"
            :value.sync="textColorField.value"
            background-class=""
            no-label
            small-inline-button
            inline-button-icon="format_color_text"
            class="pl-3"
            @update:value="$emit('changed')"
          />
        </div>
        <!-- Background Color (optional) -->
        <div
          v-if="!!backgroundColorField && isColorField(backgroundColorField)"
        >
          <overlay-data-color-field
            :class="{'pl-3': !textColorField, 'ml-2': !!textColorField}"
            :color-palette="backgroundColorFieldPalette"
            :field="backgroundColorField"
            :render-key="
              'widget-button-background-color-' + colorfieldRenderKey
            "
            :value.sync="backgroundColorField.value"
            background-class=""
            no-label
            small-inline-button
            @update:value="$emit('changed')"
          />
        </div>
      </v-layout>
      <v-layout
        align-baseline
        class="overlay-options-field-bg"
      >
        <!-- Button Size (optional) -->
        <v-slide-y-transition>
          <v-slider
            v-if="
              textField &&
                textField.value &&
                sizeField &&
                isRangeField(sizeField)
            "
            :max="sizeField.schema.maximum"
            :min="sizeField.schema.minimum"
            :step="sizeField.schema.multipleOf"
            :title="$t('buttonSize')"
            :value="sizeField.value"
            class="mt-0 px-3 pb-3"
            prepend-icon="open_in_full"
            hide-details
            @click:prepend="toggleButtonSizeValue"
            @input="onSizeChanged"
          />
        </v-slide-y-transition>
        <!-- Position Offset (optional) -->
        <v-slide-y-transition>
          <v-menu
            v-if="positionXField && positionYField"
            v-model="showPositionButtonsMenu"
            top
            class="ml-3 mr-3"
            :close-on-content-click="false"
          >
            <v-btn
              slot="activator"
              flat
              class="arrow-btn ma-0"
            >
              <v-icon class="grey--text text--darken-2">
                open_with
              </v-icon>
            </v-btn>
            <v-list class="pa-0 pa-1">
              <v-layout>
                <div class="arrow-btn" />
                <v-btn
                  flat
                  class="arrow-btn ma-0"
                  @mousedown="onPositionYDecreaseMouseDown"
                  @mouseup="onPositionYDecreaseMouseUp"
                >
                  <v-icon>arrow_upward</v-icon>
                </v-btn>
                <div class="arrow-btn" />
              </v-layout>
              <v-layout>
                <v-btn
                  flat
                  class="arrow-btn ma-0"
                  @mousedown="onPositionXDecreaseMouseDown"
                  @mouseup="onPositionXDecreaseMouseUp"
                >
                  <v-icon>arrow_back</v-icon>
                </v-btn>
                <v-btn
                  :disabled="
                    positionXField.value == 0 && positionYField.value == 0
                  "
                  flat
                  class="arrow-btn ma-0"
                  :title="$t('reset')"
                  @click="onPositionResetClicked"
                >
                  <v-icon>clear</v-icon>
                </v-btn>
                <v-btn
                  flat
                  class="arrow-btn ma-0"
                  @mousedown="onPositionXIncreaseMouseDown"
                  @mouseup="onPositionXIncreaseMouseUp"
                >
                  <v-icon>arrow_forward</v-icon>
                </v-btn>
              </v-layout>
              <v-layout>
                <div class="arrow-btn" />
                <v-btn
                  flat
                  class="arrow-btn ma-0"
                  @mousedown="onPositionYIncreaseMouseDown"
                  @mouseup="onPositionYIncreaseMouseUp"
                >
                  <v-icon>arrow_downward</v-icon>
                </v-btn>
              </v-layout>
              <div class="arrow-btn" />
            </v-list>
          </v-menu>
        </v-slide-y-transition>
      </v-layout>
    </v-layout>
  </v-form>
</template>

<script>
import {deepCopyObject} from '@/utils';

import {
  isColorField,
  isRangeField,
  OverlayOptionsField,
  OverlayOptionsFieldWarning,
} from '@/mixins/overlayOptions.js';
import overlayDataPositionButtons from '@/mixins/overlayDataPositionButtons.js';

import OverlayDataColorField from '@/components/OverlayDataColorField';
import OverlayDataTextField from '@/components/OverlayDataTextField';

export default {
  components: {
    OverlayDataColorField,
    OverlayDataTextField,
  },
  mixins: [overlayDataPositionButtons],
  props: {
    /**
     * Name of the widget as used in overlay options.
     *
     * Needed to backtrack validation status to its group.
     */
    name: {
      type: String,
      required: true,
    },
    text: {
      type: OverlayOptionsField,
      required: true,
    },
    size: {
      type: OverlayOptionsField,
      default: null,
    },
    /** Position Offset on X Axis. */
    positionX: {
      type: OverlayOptionsField,
      default: null,
    },
    /** Position Offset on Y Axis. */
    positionY: {
      type: OverlayOptionsField,
      default: null,
    },
    textColor: {
      type: OverlayOptionsField,
      default: null,
    },
    backgroundColor: {
      type: OverlayOptionsField,
      default: null,
    },
    colorPalette: {
      type: Array,
      default: null,
    },
    /**
     * Optional warning messages shown above the interface.
     */
    warnings: {
      type: Array[OverlayOptionsFieldWarning],
      default: null,
    },
  },
  data() {
    // We have to mirror the props as data attributes so they are reactive.
    // See: https://vuejs.org/v2/guide/components-props.html#One-Way-Data-Flow
    return {
      backgroundColorField: this.backgroundColor,
      positionXField: this.positionX,
      positionYField: this.positionY,
      sizeField: this.size,
      textColorField: this.textColor,
      textField: this.text,

      colorfieldRenderKey: 0,
    };
  },
  computed: {
    fields() {
      return [
        this.backgroundColorField,
        this.positionXField,
        this.positionYField,
        this.sizeField,
        this.sizeField,
        this.textColorField,
        this.textField,
      ];
    },
    providedFields() {
      return this.fields.filter((field) => !!field);
    },
    textColorFieldPalette() {
      const palette = deepCopyObject(this.colorPalette);
      // Add background_color to palette, if already specified.
      if (
        this.backgroundColorField &&
        this.backgroundColorField.value &&
        palette.indexOf(this.backgroundColorField.value.toLowerCase()) === -1
      ) {
        palette.push(this.backgroundColorField.value);
      }
      return palette;
    },
    backgroundColorFieldPalette() {
      const palette = deepCopyObject(this.colorPalette);
      // Add text_color to palette, if already specified.
      if (
        this.textColorField &&
        this.textColorField.value &&
        palette.indexOf(this.textColorField.value.toLowerCase()) === -1
      ) {
        palette.push(this.textColorField.value);
      }
      return palette;
    },
  },
  created() {
    this.registerSetValueFromOutsideEventListener();
  },
  methods: {
    isColorField: isColorField,
    isRangeField: isRangeField,
    toggleButtonSizeValue() {
      if (this.sizeField.value >= this.sizeField.schema.maximum) {
        this.sizeField.value = this.sizeField.schema.minimum;
      } else if (this.sizeField.value == this.sizeField.schema.minimum) {
        const middle =
          this.sizeField.schema.minimum +
          (this.sizeField.schema.maximum - this.sizeField.schema.minimum) / 2;
        this.sizeField.value = middle;
      } else {
        this.sizeField.value = this.sizeField.schema.maximum;
      }
      this.$emit('changed');
    },
    onSizeChanged(size) {
      this.sizeField.value = size;
      this.$emit('changed');
    },
    validate() {
      return this.$refs.form.validate();
    },
    registerSetValueFromOutsideEventListener() {
      this.$bus.$on('set-overlay-data-field-value', (fieldName, newValue) => {
        for (const field of this.providedFields) {
          if (field.name !== fieldName) {
            continue;
          }
          if (this.isColorField(field)) {
            // Force ColorPicker to rerender so the displayed color matches.
            this.$nextTick(() => {
              this.colorfieldRenderKey += 1;
            });
          }
          field.value = newValue;
        }
      });
    },
    getOverlayData() {
      const widgetOverlayData = {
        _labels: {},
      };
      for (const field of this.providedFields) {
        let value = field.value;

        widgetOverlayData[field.name] = value;
        widgetOverlayData._labels[field.name] = field.label;
      }
      return widgetOverlayData;
    },
  },
};
</script>

<style scoped>
.arrow-btn {
  min-width: 40px;
  max-width: 40px;
}
</style>



// WEBPACK FOOTER //
// src/components/OverlayDataWidgetButton.vue